import { useCallback, useEffect, useRef, useState } from 'react';
import { listVersions, type FileVersion } from '../../services/versions';

/** Pictures still being drawn are asked for again this often, this many times. */
const PENDING_RETRY_MS = 4000;
const PENDING_TRIES = 5;

/**
 * A file's versions (W5-AR1), read when asked for (the title menu opening, the panel) and kept
 * for the visit, so the menu never opens empty after the first time. Pictures still being drawn
 * are asked for again a few times; nothing is polled once they are all in.
 */
export function useVersions(documentId: string) {
  const [versions, setVersions] = useState<FileVersion[] | null>(null);
  const [more, setMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const asking = useRef<Promise<void> | null>(null);
  const tries = useRef(0);
  const timer = useRef<number | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setVersions(null);
    tries.current = 0;
    return () => {
      alive.current = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [documentId]);

  const refresh = useCallback((): Promise<void> => {
    if (asking.current) return asking.current;
    const asked = listVersions(documentId)
      .then((answer) => {
        if (!alive.current) return;
        setVersions(answer.versions);
        setMore(answer.more);
        setFailed(false);
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = null;
        if (answer.versions.some((one) => one.thumbnailPending) && tries.current < PENDING_TRIES) {
          tries.current += 1;
          timer.current = window.setTimeout(() => void refresh(), PENDING_RETRY_MS);
        } else if (!answer.versions.some((one) => one.thumbnailPending)) tries.current = 0;
      })
      .catch(() => {
        if (alive.current) setFailed(true);
      })
      .finally(() => {
        asking.current = null;
      });
    asking.current = asked;
    return asked;
  }, [documentId]);

  /** The next page of older versions, for the panel's end of list. */
  const loadMore = useCallback(async () => {
    const last = versions?.[versions.length - 1];
    if (!last || !more) return;
    const answer = await listVersions(documentId, 50, last.createdAt);
    if (!alive.current) return;
    setVersions((now) => [...(now ?? []), ...answer.versions.filter((one) => !(now ?? []).some((had) => had.id === one.id))]);
    setMore(answer.more);
  }, [documentId, more, versions]);

  /** Shown at once after a save or a rename, before the list is read again. */
  const put = useCallback((version: FileVersion) => {
    setVersions((now) => {
      const rest = (now ?? []).filter((one) => one.id !== version.id);
      return [version, ...rest].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    });
  }, []);

  return { versions, more, failed, refresh, loadMore, put };
}
